// src/routes/Routes.jsx
/**
 * Application route tree — registry-driven private modules + legacy public routes.
 *
 * Private modules: `renderModuleRoutes()` emits `<Route>` elements from
 * `getModuleRouteDefinitions()`. Page components resolve via `lazyRouteChunk(routeId)`
 * which shares the preload cache in `routeChunkLoaders.js` (loaders defined in
 * `appModuleDefinitions.js`).
 *
 * @see docs/Modules/adding-a-module.md
 * @see docs/route-chunk-preload.md
 */
import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { withSentryReactRouterV6Routing } from "observability/sentry";
import { useTranslation } from "react-i18next";
import { APP_MODULES_CONFIG } from "../utils/features";
import useAuthStore from "../stores/useAuthStore";
import {
  clearIntendedReturnUrl,
  resolveAuthorizedReturnPath,
} from "../auth";
import { logReturnUrlFlow } from "../auth/returnUrlDebug";
import AppLayout from "../components/universal/Layout/AppLayout";
import RedirectToLoginWithReturnUrl from "./RedirectToLoginWithReturnUrl";
import MobileAppLayout from "../components/universal/Layout/MobileAppLayout";
import AnimatedText from "components/universal/AnimatedText/AnimatedText";
// Module route generation (registry-driven; replaces env flags and RoleBasedRoute).
import { getModuleRouteDefinitions } from "modules/appModuleRoutes";
import PrivateRoute from "./PrivateRoute";
import RequireModuleAccess from "./RequireModuleAccess";
import {
  ROUTE_CHUNK_LOADERS_BY_ROUTE_ID,
  preloadRouteChunk,
} from "./routeChunkLoaders";
import NotFound from "../pages/notFound";
import MobileViewportNotFound from "../pages/notFound/MobileViewportNotFound.jsx";
import { useMobileViewport } from "../hooks/useMobileViewport";

// lazy-load all "page" components
const Login = lazy(() => import("../pages/login"));
const ObligationExternalCompletion = lazy(() => import("../pages/external/upload"));
const SessionObligationPage = lazy(() => import("../pages/session/obligation"));
const SessionPage = lazy(() => import("../components/Contracts/一legacy/external-session/SessionPage.jsx"));
const AddSupplierSession = lazy(() => import("../components/Contracts/一legacy/supplier/AddSupplierSession"));

const Test = lazy(() => import("../pages/test/index"));
const SentryRoutes = withSentryReactRouterV6Routing(Routes);

/**
 * React.lazy wrapper tied to the shared chunk preload cache.
 * Sidebar hover and post-login warmup call `preloadRouteChunk(routeId)` first,
 * so navigation often resolves without a Suspense flash.
 */
function lazyRouteChunk(routeId) {
  if (!ROUTE_CHUNK_LOADERS_BY_ROUTE_ID[routeId]) {
    throw new Error(`Missing route chunk loader for routeId: ${routeId}`);
  }
  return lazy(() => preloadRouteChunk(routeId));
}

// Private route without RequireModuleAccess — misconfigured evaluators must always reach it.
const AccountMisconfigured = lazy(() => import("../pages/account-misconfigured"));

// Legacy mobile modules intentionally kept for future reuse.
// const HomeMobile = lazy(() => import("../pages/mobile"));
// const MobileDashboardLayout = lazy(() => import("../components/universal/Layout/MobileDashboardLayout.jsx"));
// const MobileDashboard = lazy(() => import("../pages/mobile_dashboard/MobileDashboard.jsx"));
// const MobileObligations = lazy(() => import("../pages/mobile_obligations/MobileObligations.jsx"));
// const MobileAllObligations = lazy(() => import("../pages/mobile_all_obligations/MobileAllObligations.jsx"));
// const MobileNotFound = lazy(() => import("../pages/mobile_404/MobileNotFound.jsx"));
const TermsAndConditions = lazy(() => import("../pages/terms-and-conditions"));
// External Actions (token sign-in) deferred. Restore lazy import and route when shipped.
// const ActionExternalLogin = lazy(() =>
//   import("../pages/actions/external/ActionExternalLogin.jsx")
// );

const PAGE_LOADING_BG = "rgb(241, 242, 245)";

/** Memoize one `lazy()` component per registry `routeId` across re-renders. */
const moduleRouteComponentCache = new Map();

function PageSuspense({ children }) {
  const { t } = useTranslation("common");

  return (
    <Suspense
      fallback={
        <div
          style={{
            flex: 1,
            alignSelf: "stretch",
            width: "100%",
            minHeight: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: PAGE_LOADING_BG,
          }}
        >
          <AnimatedText
            text={`${t("spinner.loading")}...`}
            animationType="wave"
            size="medium"
          />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function AppLayoutRoute() {
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

function MobileLayoutRoute() {
  return (
    <MobileAppLayout>
      <Outlet />
    </MobileAppLayout>
  );
}

function AuthenticatedReturnRedirect({ userData }) {
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("returnTo");
  const { path } = resolveAuthorizedReturnPath(userData, returnTo || undefined);

  useEffect(() => {
    // logReturnUrlFlow("Routes:authenticated-return-redirect", {
    //   currentPath: `${location.pathname}${location.search}${location.hash}`,
    //   queryReturnTo: returnTo,
    //   resolvedPath: path,
    //   role: userData?.role,
    //   modules: userData?.modules,
    // });
    clearIntendedReturnUrl();
  }, [location.hash, location.pathname, location.search, path, returnTo, userData?.modules, userData?.role]);

  return <Navigate to={path} replace />;
}

/** Resolve lazy component for a registry routeId; fails fast if manifest is out of sync. */
function getModuleRouteComponent(routeId) {
  if (!moduleRouteComponentCache.has(routeId)) {
    moduleRouteComponentCache.set(routeId, lazyRouteChunk(routeId));
  }
  return moduleRouteComponentCache.get(routeId);
}

/** Wraps each module route with access guard + suspense boundary. */
function ModuleRouteElement({ moduleKey, Component }) {
  return (
    <RequireModuleAccess moduleKey={moduleKey}>
      <PageSuspense>
        <Component />
      </PageSuspense>
    </RequireModuleAccess>
  );
}

/**
 * Emit `<Route>` elements from {@link getModuleRouteDefinitions} output.
 * Flat modules may produce multiple routes; shell modules produce one mount route.
 */
function renderModuleRoutes(routeDefinitions) {
  return routeDefinitions.map(({ moduleKey, path, routeId }) => {
    const Component = getModuleRouteComponent(routeId);

    return (
      <Route
        key={`${moduleKey}:${routeId}:${path}`}
        path={path}
        element={<ModuleRouteElement moduleKey={moduleKey} Component={Component} />}
      />
    );
  });
}

function AppRoutes() {
  const authToken = useAuthStore((s) => s.authToken);
  const userData = useAuthStore((s) => s.userData);
  const authBootstrapStatus = useAuthStore((s) => s.authBootstrapStatus);
  const hasPendingPolicyGate = authBootstrapStatus === "policy_pending";
  const hasAuthenticatedUser = Boolean(authToken) && authBootstrapStatus === "authenticated";
  const { isMobileViewport } = useMobileViewport();
  // Mobile tree only includes modules with routing.mobileEnabled in the registry.
  const routeVariant = isMobileViewport ? "mobile" : "desktop";
  const moduleRouteDefinitions = getModuleRouteDefinitions({ variant: routeVariant });

  return (
    <Suspense
      fallback={
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <AnimatedText
            text="Loading..."
            animationType="wave"
            size="large"
          />
        </div>
      }
    >
      <SentryRoutes>
        {/* Start public routes */}
        <Route
          path="/"
          element={
            hasAuthenticatedUser ? (
              <AuthenticatedReturnRedirect userData={userData} />
            ) : hasPendingPolicyGate ? (
              <Navigate to="/terms-and-conditions" replace />
            ) : (
              <RedirectToLoginWithReturnUrl />
            )
          }
        />

        <Route
          path="/login"
          element={
            hasAuthenticatedUser ? (
              <AuthenticatedReturnRedirect userData={userData} />
            ) : hasPendingPolicyGate ? (
              <Navigate to="/terms-and-conditions" replace />
            ) : (
              <Login />
            )
          }
        />

        <Route
          path="/external/upload"
          element={<ObligationExternalCompletion />}
        />

        <Route
          path="/session/obligation/:token"
          element={
            <PageSuspense>
              <SessionObligationPage />
            </PageSuspense>
          }
        />

        {/* Legacy /mobile login route intentionally disabled in favor of responsive /login. */}
        {/* <Route path="/mobile" element={<HomeMobile />} /> */}

        <Route
          path="/required/documents/:sessionId"
          element={<SessionPage />}
        />

        <Route
          path="/supplier/required/documents/:sessionId"
          element={<AddSupplierSession />}
        />

        {/* External Actions deferred. Blocks deep links and email URLs until the flow ships. */}
        {/* <Route path="/external/actions/login" element={<ActionExternalLogin />} /> */}

        <Route
          path="/external/actions/login"
          element={<Navigate to="/login" replace />}
        />

        <Route
          path="/terms-and-conditions"
          element={<TermsAndConditions />}
        />
        {/* Legacy /mobile/terms-and-conditions intentionally disabled. */}
        {/* <Route path="/mobile/terms-and-conditions" element={<TermsAndConditions />} /> */}
        {/* End public routes */}

        <Route element={<PrivateRoute />}>
          {isMobileViewport ? (
            <Route element={<MobileLayoutRoute />}>
              <Route
                path="/account/misconfigured"
                element={
                  <PageSuspense>
                    <AccountMisconfigured />
                  </PageSuspense>
                }
              />

              {/* Registry-driven private module routes (flat + shell). */}
              {renderModuleRoutes(moduleRouteDefinitions)}
            </Route>
          ) : (
            <Route element={<AppLayoutRoute />}>
              <Route
                path="/account/misconfigured"
                element={
                  <PageSuspense>
                    <AccountMisconfigured />
                  </PageSuspense>
                }
              />

              {renderModuleRoutes(moduleRouteDefinitions)}

              {APP_MODULES_CONFIG.testPage && (
                <Route
                  path="/test"
                  element={
                    <PageSuspense>
                      <Test />
                    </PageSuspense>
                  }
                />
              )}
            </Route>
          )}
        </Route>

        {/* Legacy mobile-prefixed private routes intentionally disabled. */}
        {/* <Route element={<PrivateRoute mobile />}>...</Route> */}

        <Route
          path="*"
          element={
            hasAuthenticatedUser ? (
              isMobileViewport ? (
                <MobileAppLayout>
                  <MobileViewportNotFound />
                </MobileAppLayout>
              ) : (
                <NotFound />
              )
            ) : hasPendingPolicyGate ? (
              <Navigate to="/terms-and-conditions" replace />
            ) : (
              <RedirectToLoginWithReturnUrl />
            )
          }
        />
      </SentryRoutes>
    </Suspense>
  );
}

export default AppRoutes;
